import { TextInput, Typography } from '@gurupay/gp-ui-library';
import { useFXBicValidation } from '@pages/payments/hooks/useBicValidation.ts';
import { useIbanBicMismatchSync } from '@pages/payments/hooks/useIbanBicMismatchSync.ts';
import { memo } from 'react';
import { useController } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { FXFormFieldConfig } from 'types/operations';

import { useValidationRules } from '../hooks/useValidationRules.ts';

interface BicTextFormFieldProps {
	fieldKey: string;
	fieldConfig: FXFormFieldConfig;
	disabled?: boolean;
	ibanBicMask?: string | null;
}

export const BicTextFormField = memo(
	({ fieldKey, fieldConfig, disabled, ibanBicMask = null }: BicTextFormFieldProps) => {
		const { t } = useTranslation();
		const rules = useValidationRules(fieldKey, fieldConfig);
		const ibanBicMaskRef = useIbanBicMismatchSync(ibanBicMask, fieldKey);

		const {
			validate: validateBic,
			isValidating,
			warning,
		} = useFXBicValidation(fieldKey, ibanBicMaskRef);

		const { field, fieldState } = useController({
			name: fieldKey,
			rules: { ...rules, validate: validateBic },
		});

		return (
			<div>
				<TextInput
					{...field}
					className="animate-fade-in max-w-125"
					error={fieldState.error?.message}
					isDisabled={disabled}
					isLoading={isValidating}
					isRequired={fieldConfig.required}
					label={t(`payments.newPayment.additionalData.fx.form.${fieldConfig.label}`)}
					placeholder={fieldConfig.placeholder}
				/>
				{warning && (
					<Typography className="mt-space-4 text-status-warning-500" level="small-text">
						{warning}
					</Typography>
				)}
			</div>
		);
	},
);

BicTextFormField.displayName = 'BicTextFormField';
